import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import useAuth from "../../hooks/useAuth";

const AUTH_API = (process.env.REACT_APP_API_URL || "") + "/api/auth";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [, , setAuthTokens] = useAuth();

  const token = searchParams.get("token");

  const [inviteInfo, setInviteInfo] = useState(null);   // { email, role }
  const [inviteError, setInviteError] = useState("");
  const [validating, setValidating] = useState(true);

  const [form, setForm] = useState({ first_name: "", last_name: "", password: "", confirm: "" });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setInviteError("No invite token found. Ask your admin to resend the invite.");
      setValidating(false);
      return;
    }
    axios.get(`${AUTH_API}/invite/validate/?token=${token}`)
      .then(r => { setInviteInfo(r.data); setValidating(false); })
      .catch(err => {
        setInviteError(err.response?.data?.error || "This invite link is invalid or has expired.");
        setValidating(false);
      });
  }, [token]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setFormError("");
    if (form.password !== form.confirm) {
      setFormError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${AUTH_API}/invite/register/`, {
        token,
        first_name: form.first_name,
        last_name: form.last_name,
        password: form.password,
      });
      // Store tokens and redirect
      localStorage.setItem("authTokens", JSON.stringify(res.data));
      window.location.href = res.data.role === "admin" ? "/admin" : "/submit";
    } catch (err) {
      setFormError(err.response?.data?.error || "Registration failed. Please try again.");
      setSubmitting(false);
    }
  };

  if (validating) return <div className="container"><p>Validating invite...</p></div>;

  if (inviteError) {
    return (
      <div className="container" style={{ maxWidth: 480, margin: "4rem auto" }}>
        <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
          <h2>Invalid Invite</h2>
          <p style={{ color: "#dc3545" }}>{inviteError}</p>
          <p className="muted">Contact your administrator to request a new invite link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 480, margin: "4rem auto" }}>
      <div className="card" style={{ padding: "2rem" }}>
        <h2>Create Your Account</h2>
        <p className="muted" style={{ marginBottom: "1.5rem" }}>
          You were invited as <strong>{inviteInfo.email}</strong>.
        </p>
        {inviteInfo.username && (
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label>Your Username</label>
            <input value={inviteInfo.username} readOnly style={{ background: "var(--color-surface-alt, #f5f5f5)", cursor: "default" }} />
            <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
              This was assigned by your admin — you'll use this to log in.
            </p>
          </div>
        )}
        {formError && <p className="msg-error">{formError}</p>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>First Name</label>
            <input
              name="first_name" required
              value={form.first_name} onChange={handleChange}
              placeholder="First name"
            />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input
              name="last_name" required
              value={form.last_name} onChange={handleChange}
              placeholder="Last name"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password" name="password" required
              value={form.password} onChange={handleChange}
              placeholder="At least 8 characters"
            />
          </div>
          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password" name="confirm" required
              value={form.confirm} onChange={handleChange}
              placeholder="Repeat password"
            />
          </div>
          <button type="submit" className="btn-primary" disabled={submitting} style={{ width: "100%", marginTop: "1rem" }}>
            {submitting ? "Creating account..." : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
}
